import React, { useCallback } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import Typography from '@ui/Typography';
import TypographyLink from '@ui/TypographyLink';
import { useEventsContext } from '../../../context/events/EventsContext';
import { HelpCopyProps } from '../sources';

const useStyles = makeStyles()(theme => ({
  fullWidthContainer: {
    width: '100%',
    backgroundColor: palette.gray_1100,
    borderBottom: `1px solid ${theme.palette.grey[800]}`,
    borderTop: `1px solid ${theme.palette.grey[800]}`,
    padding: theme.spacing(1.5, 0),
  },
  container: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1, 0.5),
    margin: theme.spacing(2.5),
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'row',
    border: `1px solid ${theme.palette.grey[800]}`,
    borderRadius: theme.spacing(0.5),
  },
}));

const useContentStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
  },
  link: {
    padding: theme.spacing(0, 0, 0, 0.5),
  },
}));

export interface HelpSectionProps extends HelpCopyProps {
  tooltip?: boolean;
  onClick?: () => void;
  variant?: 'fullWidth' | 'badge';
}

export type HelpSectionContentProps = Omit<HelpSectionProps, 'variant'>;

/**
 * Renders text content + link
 */
export const HelpSectionContent = (props: HelpSectionContentProps) => {
  const { classes } = useContentStyles();
  const { text, ctaText, href, trackingEvent, onClick, tooltip } = props;
  const [eventTracking] = useEventsContext();

  const handleClick = useCallback(() => {
    if (trackingEvent) {
      eventTracking.track({
        type: trackingEvent,
        data: {
          action: 'linkClick',
        },
      });
    }

    if (onClick) {
      onClick();
    }
  }, [eventTracking, onClick, trackingEvent]);

  return (
    <Box display="flex" justifyContent="center" gap={0.5}>
      <Typography
        className={tooltip ? '' : classes.container}
        variant={'pSm'}
        color="primary"
      >
        {text}{' '}
      </Typography>
      <TypographyLink
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        variant={'pSm'}
        color="primary"
      >
        {ctaText}
      </TypographyLink>
    </Box>
  );
};

/**
 * Renders styled box around help section content
 */
const HelpSection = (props: HelpSectionProps) => {
  const { tooltip, variant = 'badge' } = props;
  const { classes } = useStyles();
  const containerClass =
    variant === 'fullWidth' ? classes.fullWidthContainer : classes.container;
  return (
    <Box className={tooltip ? '' : containerClass}>
      <HelpSectionContent {...props} />
    </Box>
  );
};

export default HelpSection;
